'use client';
import React from 'react';
import { ImageProps } from '@repo/ui/src/types/image';
import ProductOverlay from '@components/Product/Overlay';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import { YoutubeLinkProps } from '@repo/ui/src/types/youtubeLink';
import LottiePlayer from '@components/LottiePlayer';
import Accordion, { AccordionItem } from '@repo/ui/src/components/Accordion';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { BackgroundColor } from './_enums';
import { classNames } from '@repo/ui/src/utils/classNames';
import BackgroundVideo from '@repo/ui/src/components/BackgroundVideo';
import Image from 'next/image';

interface Feature {
  heading: string;
  description: string;
  youtubeLink?: YoutubeLinkProps;
  dropdownImage: ImageProps;
  overlay: ImageProps;
  animationUrl?: string;
}

interface FeatureExp extends Feature {
  isOpen: boolean;
}

interface Props {
  features: Feature[];
  backgroundTheme: BackgroundColor;
  sectionHeader: ToutContentProps;
}

const ProductAccordion = ({ features: _features, backgroundTheme, sectionHeader }: Props) => {
  const [width, setWidth] = React.useState(0);
  const videoContainer = React.useRef<HTMLDivElement>(null);
  const [features, setFeatures] = React.useState<FeatureExp[]>(
    _features?.map((feature, i) => ({ ...feature, isOpen: i === 0 })) ?? [],
  );
  const dark = backgroundTheme === 'Black' || backgroundTheme === 'Dark';

  React.useEffect(() => {
    if (videoContainer.current) {
      setWidth(videoContainer.current.offsetWidth);
    }
  }, [videoContainer]);

  const onClick = React.useCallback(
    ([index]: any) => {
      setFeatures((state) => {
        return state.map((feat, i) =>
          i === index ? { ...feat, isOpen: true } : { ...feat, isOpen: false },
        );
      });
    },
    [setFeatures],
  );

  const getBgColor = () => {
    if (backgroundTheme === 'Black' || backgroundTheme === 'Dark') {
      return 'tw-bg-neutral-800';
    }
    return 'tw-bg-warm-200';
  };

  return (
    <div 
      className={classNames(
        'tw-grid tw-grid-rows-[min-content] tw-relative tw-py-20',
        'md:tw-grid-cols-[1fr_32%] md:tw-grid-rows-1 md:tw-py-[110px]',
        'xl:tw-grid-cols-[1fr_30%] xl:tw-py-[145px]',
        getBgColor()
      )}
    >
      <div className="tw-relative">
        <div className="tw-aspect-video tw-sticky tw-top-[140px] md:tw-aspect-[5/4]">
          <ToutContent
            align="left"
            headingLight={sectionHeader?.headingLight}
            heading={sectionHeader?.heading}
            headerVariation={sectionHeader?.headerVariation}
            dark={dark}
            className="tw-ml-6 md:tw-z-[1] md:tw-ml-10 lg:tw-ml-20 tout-content"
          />
          <div className="tw-relative tw-w-full tw-h-full md:tw-mt-[-25%]">
            <ProductOverlay
              images={features.map((feat) => ({
                overlayImage: feat?.overlay,
                checked: feat?.isOpen,
              }))}
              imageWidth={1100}
              display
            />
            {features
              .filter((feat) => !!feat?.animationUrl)
              .map((feat) => (
                <LottiePlayer
                  key={feat?.heading}
                  animationUrl={feat.animationUrl!}
                  isPlaying={feat.isOpen}
                  isStopped={!feat.isOpen}
                />
              ))}
          </div>
        </div>
      </div>
      <div className="tw-px-6 md:tw-pr-10 md:tw-pl-0 lg:tw-pr-20 lg:tw-pl-0">
        <Accordion
          preExpanded={[0]}
          onChange={onClick}
          divider
          dark={dark}
          className="tw-border-neutral-300 last:tw-border-b"
        >
          {features.map((item, i) => {
            const dropdownImage =
              item.dropdownImage?.image && buildImageSrc(item.dropdownImage, 500);
            return (
              <AccordionItem
                key={i}
                uuid={i}
                heading={item.heading}
                headerClassName="tw-border-t tw-border-neutral-300"
              >
                <p
                  className={classNames(
                    dark ? 'tw-text-neutral-100' : 'tw-text-neutral-700',
                    'tra-text-web-body-xl',
                  )}
                >
                  {item.description}
                </p>
                {(item.youtubeLink?.videoAsset || item.dropdownImage) && (
                  <div className="tw-mt-4 tw-w-full md:tw-mt-6" ref={videoContainer}>
                    {item.youtubeLink?.videoAsset && (
                      <BackgroundVideo
                        video={item.youtubeLink.videoAsset}
                        width={width}
                        fillContainer
                        playButton
                      />
                    )}
                    {dropdownImage && (
                      <div className="tw-relative tw-aspect-video">
                        <Image
                          src={dropdownImage}
                          fill
                          objectFit='cover'
                          priority={true}
                          alt={item.dropdownImage.altTag || ''}
                        />
                      </div>
                    )}
                  </div>
                )}
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </div>
  );
};

export default ProductAccordion;